import { useEffect, useState, useCallback } from "react";
import api, { apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { copyText } from "@/lib/clipboard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PaymentHistory from "@/components/PaymentHistory";
import { Lock, Unlock, Loader2, Copy, RefreshCw, Printer, CheckCircle2, CreditCard, ExternalLink } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function PendingLink({ tx, onCheck, checking }) {
  return (
    <div className="mt-3 rounded-lg border border-border bg-background/40 p-3 space-y-2" data-testid="pending-payment">
      <div className="text-xs text-muted-foreground">
        Waiting for payment of <b className="text-white">{money(tx.amount)}</b>
        {tx.emailed_to ? <> · emailed to <span className="text-white" data-testid="payment-emailed-to">{tx.emailed_to}</span></> : " · no email on file, share the link manually"}
      </div>
      <div className="flex gap-2 flex-wrap">
        <code className="text-[11px] bg-secondary px-2 py-1.5 rounded truncate max-w-full sm:max-w-md" data-testid="payment-link-text">{tx.checkout_url}</code>
        <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => copyText(tx.checkout_url, "Payment link copied")} data-testid="copy-payment-link-btn"><Copy className="h-3.5 w-3.5 mr-1" /> Copy Link</Button>
        <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => window.open(tx.checkout_url, "_blank")} data-testid="open-payment-link-btn"><ExternalLink className="h-3.5 w-3.5" /></Button>
        <Button size="sm" variant="secondary" className="rounded-lg" onClick={onCheck} disabled={checking} data-testid="check-payment-btn">{checking ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Check Payment</Button>
      </div>
    </div>
  );
}

export default function PrintUnlockPanel({ clientId, onStatus, onChanged }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [waive, setWaive] = useState(false);
  const [customFee, setCustomFee] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/print-status`);
      setSt(data);
      onStatus?.(data.unlocked);
    } catch { /* ignore */ }
  }, [clientId, onStatus]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!st?.pending) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [st?.pending, load]);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); } finally { setBusy(""); }
  };
  const requestPayment = () => run("req", async () => {
    const override = waive ? 0 : (customFee !== "" ? Math.max(0, parseFloat(customFee) || 0) : null);
    const { data } = await api.post(`/clients/${clientId}/print-payment`, { origin_url: window.location.origin, service_fee_override: override });
    toast.success(data.emailed_to ? `Payment link emailed to ${data.emailed_to}` : "Payment link created — copy and send it to the client");
    await load();
  });
  const check = () => run("check", async () => { await load(); });
  const printBatch = () => window.open(`${API}/clients/${clientId}/print-batch?auth=${encodeURIComponent(localStorage.getItem("cp_token") || "")}`, "_blank");
  const markMailed = () => run("mailed", async () => {
    await api.post(`/clients/${clientId}/print-batch/mailed`);
    toast.success("Round closed — the next round needs a new payment");
    await load();
    onChanged?.();
  });

  if (!st) return null;
  const unlocked = st.unlocked;
  const fee = waive ? 0 : (customFee !== "" ? Math.max(0, parseFloat(customFee) || 0) : st.service_fee);
  const total = fee + st.certified_mail_fee;
  return (
    <Card className={`p-4 mb-3 border ${unlocked ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`} data-testid="print-unlock-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${unlocked ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
            {unlocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
          </div>
          <div>
            <div className="font-semibold text-sm flex items-center gap-2" data-testid="print-lock-status">
              {unlocked ? "Printing unlocked for this round" : "Printing locked"}
              {unlocked && <Badge className="bg-emerald-500/20 text-emerald-300 text-[10px]">Paid {money(st.active.amount)}</Badge>}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5" data-testid="print-fee-breakdown">
              Service {money(unlocked ? st.active.service_fee : fee)}{!unlocked && waive && " (waived)"} + Certified mail w/ Return Receipt {money(unlocked ? st.active.certified_mail_fee : st.certified_mail_fee)} = <b className="text-white">{money(unlocked ? st.active.amount : total)}</b>
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5" data-testid="mail-fee-source">
              {st.mail_fee_source === "usps_live" ? `Live USPS rate from ZIP ${st.client_zip} to all 3 bureaus` : "USPS Certified Mail + Return Receipt for 3 letters (current USPS prices)"}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {unlocked ? (
            <>
              <Button size="sm" className="rounded-lg" onClick={printBatch} data-testid="print-batch-btn"><Printer className="h-3.5 w-3.5 mr-1" /> Print 3 Bureau Letters</Button>
              <Button size="sm" variant="secondary" className="rounded-lg" onClick={markMailed} disabled={busy === "mailed"} data-testid="mark-mailed-btn">{busy === "mailed" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />} Mark as Mailed</Button>
            </>
          ) : (
            <Button size="sm" className="rounded-lg" onClick={requestPayment} disabled={busy === "req"} data-testid="request-payment-btn">
              {busy === "req" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CreditCard className="h-3.5 w-3.5 mr-1" />} {st.pending ? `Send New Link (${money(total)})` : `Request Payment (${money(total)})`}
            </Button>
          )}
        </div>
      </div>
      {!unlocked && (
        <div className="mt-3 pt-3 border-t border-border/60 flex flex-wrap items-center gap-4 text-xs" data-testid="fee-adjust-row">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={waive} onChange={(e) => setWaive(e.target.checked)} className="accent-primary h-4 w-4" data-testid="waive-fee-checkbox" />
            Waive my service fee for this client
          </label>
          <label className={`flex items-center gap-2 ${waive ? "opacity-40" : ""}`}>
            Custom fee $
            <input type="number" min="0" step="0.01" value={customFee} disabled={waive} placeholder={st.service_fee.toFixed(2)} onChange={(e) => setCustomFee(e.target.value)} className="w-24 rounded-md bg-background/60 border border-border px-2 py-1 font-mono" data-testid="custom-fee-input" />
          </label>
        </div>
      )}
      {!unlocked && st.pending && <PendingLink tx={st.pending} onCheck={check} checking={busy === "check"} />}
      <PaymentHistory history={st.history} />
    </Card>
  );
}
